import { RequireRoasterAdmin, RequireSFDCAuth } from "@client-shared/providers/auth";
import type { AccessibleModule } from "@shared/types";
import type React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AuthRoutes from "./components/Auth";
import { ProtectedRoute } from "./components/common";
import { useRBAC } from "./contexts/rbac-context";
import { ROUTE_PATHS } from "./lib/constants";
import {
  AdminCrossBorderPage,
  AdminProductsPage,
  AdminRoastersPage,
  AnalyticsPage,
  BatchesPage,
  BatchSummaryPage,
  ContactUsPage,
  CustomersPage,
  DashboardPage,
  FinanceDashboardPage,
  FinanceInvoicesPage,
  FinanceSettingsPage,
  HomePage,
  InventoryPage,
  LabelsPage,
  NotificationsPage,
  OnboardingMapProductsPage,
  OnboardingPage,
  OnboardingSuccessPage,
  OrderWaitingSummaryPage,
  OrdersPage,
  PreferencesPage,
  ProductsPage,
  ShopifyCallbackPage,
} from "./routes";

interface GuardedRouteConfig {
  readonly path: string;
  readonly requiredPage: string;
  readonly requiredModule?: AccessibleModule;
  readonly element: React.ReactElement;
}

const guardedRoutes: readonly GuardedRouteConfig[] = [
  {
    path: ROUTE_PATHS.DASHBOARD,
    requiredPage: "dashboard.view",
    requiredModule: "dashboard",
    element: <DashboardPage />,
  },
  {
    path: ROUTE_PATHS.PRODUCTS,
    requiredPage: "products.view",
    requiredModule: "products",
    element: <ProductsPage />,
  },
  {
    path: `${ROUTE_PATHS.ORDERS}/:purchaseOrderId?`,
    requiredPage: "orders.view",
    requiredModule: "orders",
    element: <OrdersPage />,
  },
  {
    path: `${ROUTE_PATHS.MARKETPLACE}/:carrierAccountId/orders/:purchaseOrderId?`,
    requiredPage: "orders.view",
    requiredModule: "orders",
    element: <OrdersPage />,
  },
  {
    path: ROUTE_PATHS.ORDERS_WAITING_SUMMARY,
    requiredPage: "orders.view",
    requiredModule: "orders",
    element: <OrderWaitingSummaryPage />,
  },
  {
    path: `${ROUTE_PATHS.MARKETPLACE}/:carrierAccountId/orders/waiting-summary`,
    requiredPage: "orders.view",
    requiredModule: "orders",
    element: <OrderWaitingSummaryPage />,
  },
  {
    path: ROUTE_PATHS.BATCHES,
    requiredPage: "batch.view",
    requiredModule: "batch",
    element: <BatchesPage />,
  },
  {
    path: ROUTE_PATHS.BATCH_SUMMARY,
    requiredPage: "batch.view",
    requiredModule: "batch",
    element: <BatchSummaryPage />,
  },
  {
    path: ROUTE_PATHS.ANALYTICS,
    requiredPage: "dashboard.view",
    requiredModule: "dashboard",
    element: <AnalyticsPage />,
  },
  {
    path: ROUTE_PATHS.CUSTOMERS,
    requiredPage: "orders.view",
    requiredModule: "orders",
    element: <CustomersPage />,
  },
  {
    path: ROUTE_PATHS.INVENTORY,
    requiredPage: "products.view",
    requiredModule: "products",
    element: <InventoryPage />,
  },
  {
    path: ROUTE_PATHS.LABELS,
    requiredPage: "labels.view",
    requiredModule: "labels",
    element: <LabelsPage />,
  },
  {
    path: ROUTE_PATHS.NOTIFICATIONS,
    requiredPage: "notifications.view",
    requiredModule: "notifications",
    element: <NotificationsPage />,
  },
  {
    path: ROUTE_PATHS.PREFERENCES,
    requiredPage: "settings.view",
    requiredModule: "settings",
    element: <PreferencesPage />,
  },
  {
    path: ROUTE_PATHS.FINANCE,
    requiredPage: "finance.invoices.view",
    requiredModule: "finance",
    element: <FinanceDashboardPage />,
  },
  {
    path: ROUTE_PATHS.FINANCE_INVOICES,
    requiredPage: "finance.invoices.view",
    requiredModule: "finance",
    element: <FinanceInvoicesPage />,
  },
  {
    path: ROUTE_PATHS.FINANCE_SETTINGS,
    requiredPage: "finance.view",
    requiredModule: "finance",
    element: <FinanceSettingsPage />,
  },
];

function BlockShipstationFromOnboarding({ children }: { readonly children: React.ReactElement }) {
  const { featureName } = useRBAC();
  if (featureName === "SS") {
    return <Navigate to={ROUTE_PATHS.HOME} replace />;
  }
  return children;
}

const AppRoutes = () => (
  <Routes>
    <Route path={ROUTE_PATHS.OAUTH} element={<AuthRoutes />} />
    <Route element={<RequireSFDCAuth />}>
      {/* Onboarding routes - accessible without installation, blocked for SS Roasters */}
      <Route
        path={ROUTE_PATHS.ONBOARDING}
        element={
          <BlockShipstationFromOnboarding>
            <OnboardingPage />
          </BlockShipstationFromOnboarding>
        }
      />
      <Route path={ROUTE_PATHS.CONTACT_US} element={<ContactUsPage />} />
      <Route
        path={ROUTE_PATHS.ONBOARDING_STEP_2}
        element={
          <BlockShipstationFromOnboarding>
            <OnboardingMapProductsPage />
          </BlockShipstationFromOnboarding>
        }
      />
      <Route
        path={ROUTE_PATHS.ONBOARDING_SUCCESS}
        element={
          <BlockShipstationFromOnboarding>
            <OnboardingSuccessPage />
          </BlockShipstationFromOnboarding>
        }
      />
      <Route
        path={ROUTE_PATHS.ONBOARDING_SHOPIFY_CALLBACK}
        element={
          <BlockShipstationFromOnboarding>
            <ShopifyCallbackPage />
          </BlockShipstationFromOnboarding>
        }
      />

      <Route element={<RequireRoasterAdmin />}>
        <Route path={ROUTE_PATHS.ADMIN_ROASTERS} element={<AdminRoastersPage />} />
        <Route path={ROUTE_PATHS.ADMIN_PRODUCTS} element={<AdminProductsPage />} />
        <Route path={ROUTE_PATHS.ADMIN_CROSS_BORDER} element={<AdminCrossBorderPage />} />
      </Route>

      {/* Protected routes - installation check is handled by AppLayout */}
      <Route path={ROUTE_PATHS.HOME} element={<HomePage />} />
      {guardedRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <ProtectedRoute requiredPage={route.requiredPage} requiredModule={route.requiredModule}>
              {route.element}
            </ProtectedRoute>
          }
        />
      ))}
      <Route path="*" element={<Navigate to={ROUTE_PATHS.HOME} replace />} />
    </Route>
  </Routes>
);

export default AppRoutes;
